import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import { isValidUrl } from 'components/forms/inputs/rhf-validators/validators';
import { HttpsLinkInput } from './HttpsLinkInput';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useReceiveClientIdentificationMutation } from 'store/api/client-identifications';
import { ClientIdentificationPanelDto } from 'types/clientIdentification';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { t } from 'utils/getTranslations';
import { missingSummary } from './missingItems';
import { confirmedByReceipt, notInFuture } from './receiptDates';
import { successAlert } from 'utils/alert';

interface FormValues {
    receivedOn: string | null;
    clientConfirmedOn: string | null;
    clientConfirmationLink: string;
}

interface Props {
    contractId: number;
    panel: ClientIdentificationPanelDto;
    onClose: () => void;
}

/**
 * Records the client's signed copy and locks the client identification. What is still missing is listed, and does not
 * stop the receipt: a blank on the client's form is recorded as a blank.
 */
export function MarkAsReceivedDialog({ contractId, panel, onClose }: Props) {
    const intl = useIntl();
    const [today] = useState(() => new Date());
    const [receive, { isLoading }] = useReceiveClientIdentificationMutation();
    const { control, handleSubmit, getValues } = useForm<FormValues>({
        defaultValues: {
            receivedOn: formatDateStringInput(today),
            clientConfirmedOn: null,
            clientConfirmationLink: '',
        },
    });

    const submit = handleSubmit(async (values) => {
        try {
            await receive({
                id: contractId,
                receivedOn: values.receivedOn ?? '',
                clientConfirmedOn: values.clientConfirmedOn || null,
                clientConfirmationLink: values.clientConfirmationLink.trim() || null,
            }).unwrap();
        } catch {
            // The API layer has shown the server's reason; the dialog stays open.
            return;
        }
        successAlert(t('client-identification.marked-received'));
        onClose();
    });

    const summary = missingSummary(panel, intl);

    return (
        <>
            <ModalHeader title={t('client-identification.mark-as-received')} onCancel={onClose} />
            <form
                noValidate
                className="flex min-h-0 flex-col gap-4 overflow-y-auto p-6"
                onSubmit={(e) => void submit(e)}
            >
                <p className="text-body1 m-0">{t('client-identification.receive-intro')}</p>
                {summary ? (
                    <p className="text-body1 text-fg-warning new:text-warning-ink m-0 font-medium">{summary}</p>
                ) : (
                    <p className="text-body1 m-0 font-medium">{t('client-identification.nothing-missing')}</p>
                )}
                <DateInput
                    control={control}
                    name="receivedOn"
                    label="client-identification.received-on"
                    required
                    maxDate={today}
                    validate={(value) => notInFuture(value, today)}
                    clearable={false}
                />
                <DateInput
                    control={control}
                    name="clientConfirmedOn"
                    label="client-identification.client-confirmed-on"
                    maxDate={today}
                    validate={(value) => confirmedByReceipt(value, getValues('receivedOn'), today)}
                />
                <HttpsLinkInput
                    control={control}
                    name="clientConfirmationLink"
                    label="client-identification.client-confirmation-link"
                    validate={(value) =>
                        !value.trim() ? true : isValidUrl(value.trim(), t('validation.url-must-be-valid'))
                    }
                />
            </form>
            <ModalFooter
                onCancel={onClose}
                onSave={() => void submit()}
                saveButtonTextId="client-identification.mark-as-received"
                disableSave={isLoading}
            />
        </>
    );
}
